/**
 * Workspace Validation Helper
 * 
 * Provides standard guards for workspace-dependent operations
 * to prevent "workspaceId is not defined" errors and handle "All" mode.
 */

import { toast } from 'sonner';

export interface WorkspaceValidationResult {
  isValid: boolean;
  workspaceId: string | null;
  errorMessage?: string;
}

/**
 * Validates that a workspace is available for create/import operations.
 * 
 * Rules:
 * - workspaceId must be truthy
 * - mode must be 'single' (not 'all')
 * 
 * @param activeWorkspaceId - The currently active workspace ID
 * @param mode - The workspace scope mode ('single' | 'all')
 * @param operationName - Name of the operation (for error messages)
 * @returns Validation result with workspaceId and error message if invalid
 */
export function validateWorkspaceForCreate(
  activeWorkspaceId: string | null | undefined,
  mode: 'single' | 'all',
  operationName: string = 'this operation'
): WorkspaceValidationResult {
  // Check if workspaceId is missing
  if (!activeWorkspaceId) {
    const errorMessage = `Select a workspace to ${operationName}.`;
    toast.error('Workspace Required', {
      description: errorMessage,
    });
    return {
      isValid: false,
      workspaceId: null,
      errorMessage,
    };
  }

  // Check if mode is 'all' (aggregation mode - cannot create/import)
  if (mode === 'all') {
    const errorMessage = `Cannot ${operationName} in "All" mode. Please select a specific workspace.`;
    toast.error('Workspace Selection Required', {
      description: errorMessage,
    });
    return {
      isValid: false,
      workspaceId: null,
      errorMessage,
    };
  }

  return {
    isValid: true,
    workspaceId: activeWorkspaceId,
  };
}

/**
 * Validates workspace for read operations (can work with 'all' mode).
 * 
 * @param activeWorkspaceId - The currently active workspace ID (can be null in 'all' mode)
 * @param mode - The workspace scope mode
 * @returns Validation result
 */
export function validateWorkspaceForRead(
  activeWorkspaceId: string | null | undefined,
  mode: 'single' | 'all'
): WorkspaceValidationResult {
  // For read operations, 'all' mode is valid (no specific workspace needed)
  if (mode === 'all') {
    return {
      isValid: true,
      workspaceId: null, // No specific workspace in 'all' mode
    };
  }

  // For 'single' mode, workspaceId must be present
  if (!activeWorkspaceId) {
    return {
      isValid: false,
      workspaceId: null,
      errorMessage: 'No workspace selected',
    };
  }

  return {
    isValid: true,
    workspaceId: activeWorkspaceId,
  };
}

